import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CameraEngine, { BACK, CameraError, FRONT, cameraErrorMessage, CAMERA_ERROR_UNSUPPORTED } from './CameraEngine'
import ClipRecorder from './videoRecorder'
import { STAGED_UPLOADING, UPLOAD_PHASE_FINALIZING } from './stagedUpload'
import { currentNetworkTier, videoCapturePlan } from './mediaQuality'
import { CloseIcon, FlipIcon, GridIcon, TimerIcon, TorchIcon, VideoIcon } from './CameraIcons'
import { resolveCameraChrome } from './cameraChrome'
import { loadCameraSelection, writeCameraSession } from './cameraSession'
import './CameraViewfinder.css'

const PHASE_STARTING = 'starting'
const PHASE_LIVE = 'live'
const PHASE_COUNTDOWN = 'countdown'
const PHASE_CAPTURING = 'capturing'
const PHASE_RECORDING = 'recording'
const PHASE_REVIEW = 'review'
const PHASE_ERROR = 'error'

const TIMER_CHOICES = [0, 3, 10]
const DIGITAL_ZOOM_MAX = 4
const HOLD_TO_RECORD_MS = 400
/** Ignore shutter after open so the Open Camera click's pointerup cannot fire a take. */
const SHUTTER_GUARD_MS = 450

const streamIsLive = (stream) =>
  !!stream && (stream.getTracks?.() || []).some((track) => track.readyState === 'live')

const grabPreviewFrame = (video) => {
  if (!video?.videoWidth || !video.videoHeight) return null
  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const context = canvas.getContext('2d')
  if (!context) return null
  try {
    context.drawImage(video, 0, 0)
    return canvas.toDataURL('image/jpeg', 0.82)
  } catch {
    return null
  }
}

const DEFAULT_LABELS = {
  close: 'Close',
  shutter: 'Take photo',
  record: 'Record',
  stop: 'Stop',
  flip: 'Switch camera',
  torch: 'Flash',
  grid: 'Grid',
  timer: 'Self-timer',
  retake: 'Retake',
  share: 'Share',
  sharing: 'Uploading…',
  finishing: 'Finishing…',
  stillUploading: 'Still uploading…',
  cancelUpload: 'Cancel upload',
  zoom: 'Zoom',
  photoHint: 'Tap the shutter when you are ready.',
  videoHint: 'Tap to start/stop, or press and hold to record.',
  ready: 'Ready to capture',
  holdLonger: 'Keep holding — clips need a few seconds.',
  capturing: 'Capturing…'
}


function distanceBetween(touches) {
  const [a, b] = touches
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

/**
 * Local review player for a just-recorded clip. MediaRecorder blobs (especially Safari
 * fMP4) often show black / 0:00 until a frame is forced and the camera pipeline is idle.
 */
function ReviewClip({ url, durationSecs = 0 }) {
  const reviewRef = useRef(null)

  useEffect(() => {
    const el = reviewRef.current
    if (!el || !url) return undefined

    try {
      el.pause?.()
    } catch {
      // jsdom / odd engines
    }
    el.src = url
    try {
      el.load?.()
    } catch {
      // jsdom does not implement media load
    }

    const revealFrame = () => {
      try {
        if (el.currentTime < 0.05) el.currentTime = 0.05
      } catch {
        // Some engines reject seeks before readyState advances.
      }
      const played = el.play?.()
      if (played?.catch) played.catch(() => {})
    }

    el.addEventListener('loadedmetadata', revealFrame)
    el.addEventListener('loadeddata', revealFrame)
    return () => {
      el.removeEventListener('loadedmetadata', revealFrame)
      el.removeEventListener('loadeddata', revealFrame)
    }
  }, [url])

  return (
    <>
      <video
        ref={reviewRef}
        className="anear-camera-review"
        controls
        playsInline
        muted
        preload="auto"
      />
      {durationSecs > 0 ? (
        <div className="anear-camera-review-duration">
          {durationSecs}s
        </div>
      ) : null}
    </>
  )
}

function UploadChip({
  label,
  percent = 0,
  showMeter = false,
  error = false,
  stalled = false,
  prominent = false
}) {
  const trackClass = [
    'anear-camera-upload-track',
    percent > 0 ? '' : 'is-indeterminate',
    stalled && percent > 0 ? 'is-stalled' : ''
  ].filter(Boolean).join(' ')

  return (
    <div
      className={[
        'anear-camera-upload',
        prominent ? 'is-prominent' : '',
        error ? 'is-error' : ''
      ].filter(Boolean).join(' ')}
      data-anear-camera-indicator=""
      role="status"
      aria-live="polite"
    >
      <div className="anear-camera-upload-row">
        <span className="anear-camera-upload-label">{label}</span>
        {!error && percent > 0 && percent < 95 ? (
          <span className="anear-camera-upload-pct">{percent}%</span>
        ) : null}
      </div>
      {showMeter && !error ? (
        <div
          className={trackClass}
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent > 0 ? percent : undefined}
        >
          <div
            className="anear-camera-upload-fill"
            style={percent > 0 ? { width: `${percent}%` } : undefined}
          />
        </div>
      ) : null}
    </div>
  )
}

/**
 * A fullscreen camera. Owns the preview, the controls, capture, and the review gate;
 * hands the finished take back to its host component, which owns upload and form
 * serialization.
 */
export default function CameraViewfinder({
  mode = 'photo',
  modes = null,
  onModeChange,
  eventId = '',
  initialFacingMode = FRONT,
  // Default on so the saved still matches the mirrored front-camera preview (no
  // surprise flip on review). Pass false when lens-true orientation matters (text/logos).
  mirrorCapture = true,
  maxSeconds = 20,
  minSeconds = 0,
  maxBitrate = 0,
  /** When true, chrome fills the EventFrame; when false, fills the viewport (body fallback). */
  contained = true,
  labels: labelOverrides = null,
  share = null,
  stayOpen = false,
  chrome = 'metal',
  registerCommands = null,
  onCaptured,
  onRetake,
  onShare,
  onCancelUpload,
  onClose
}) {
  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...(labelOverrides || {}) }), [labelOverrides])
  const isVideo = mode === 'video'
  const enabledModes = Array.isArray(modes) && modes.length ? modes : [mode]
  const showModeSwitch = enabledModes.includes('photo') && enabledModes.includes('video')
  const look = resolveCameraChrome(chrome)
  const showAccessoryTools = look !== 'minimal'
  const showZoomRail = look !== 'minimal'

  const engineRef = useRef(null)
  const videoRef = useRef(null)
  const recorderRef = useRef(null)
  const stageRef = useRef(null)
  const pinchRef = useRef({ startDistance: 0, startZoom: 1 })
  const lastTapRef = useRef(0)
  const pressStartRef = useRef(0)
  const shutterPressedRef = useRef(false)
  const shutterReadyAtRef = useRef(0)
  const holdTimerRef = useRef(null)
  const holdModeRef = useRef(false)
  const countdownRef = useRef(null)
  const capturedUrlRef = useRef('')
  const capturingRef = useRef(false)
  const selectionRef = useRef(loadCameraSelection(eventId, initialFacingMode))
  const shotHoldRef = useRef(false)
  const sawUploadRef = useRef(false)
  const uploadLockRef = useRef(false)

  const [phase, setPhase] = useState(PHASE_STARTING)
  const [errorMessage, setErrorMessage] = useState('')
  const [capabilities, setCapabilities] = useState(null)
  const [facing, setFacing] = useState(selectionRef.current.facing)
  const [holdingShot, setHoldingShot] = useState(false)
  const [activeDeviceId, setActiveDeviceId] = useState(selectionRef.current.deviceId)
  const [chips, setChips] = useState([])
  const [canFlip, setCanFlip] = useState(false)
  const [zoom, setZoom] = useState(selectionRef.current.zoom ?? 1)
  const [torchOn, setTorchOn] = useState(selectionRef.current.torch)
  const [gridOn, setGridOn] = useState(false)
  const [timerSecs, setTimerSecs] = useState(0)
  const [countdown, setCountdown] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [recPulse, setRecPulse] = useState(true)
  const [focusPoint, setFocusPoint] = useState(null)
  const [captured, setCaptured] = useState(null)
  // Set only once the guest actually taps Share. The background transfer that starts at
  // the shutter must never present itself as something the guest is waiting on.
  const [committing, setCommitting] = useState(false)
  const [shareError, setShareError] = useState('')
  const [shutterHint, setShutterHint] = useState('')
  const [freeze, setFreeze] = useState(null)
  const [uploadStalled, setUploadStalled] = useState(false)
  uploadLockRef.current = stayOpen && (holdingShot || share?.status === STAGED_UPLOADING)

  const opticalZoom = capabilities?.zoom || null
  const zoomMin = opticalZoom?.min ?? 1
  const zoomMax = opticalZoom?.max ?? DIGITAL_ZOOM_MAX
  const digitalZoom = opticalZoom ? 1 : zoom
  const mirrorPreview = facing === FRONT

  const releaseCapturedUrl = useCallback(() => {
    if (capturedUrlRef.current) {
      URL.revokeObjectURL(capturedUrlRef.current)
      capturedUrlRef.current = ''
    }
  }, [])

  const attachStream = useCallback((stream) => {
    const element = videoRef.current
    if (!element || !stream) return
    if (element.srcObject !== stream) element.srcObject = stream
    element.muted = true
    element.playsInline = true
    const played = element.play?.()
    if (played?.catch) played.catch(() => {})
  }, [])

  const rememberSelection = useCallback((patch) => {
    selectionRef.current = { ...selectionRef.current, ...patch }
    writeCameraSession(eventId, selectionRef.current)
  }, [eventId])

  const openCamera = useCallback(
    async ({ facingMode, deviceId = '' }) => {
      const engine = engineRef.current
      if (!engine) return

      setPhase(PHASE_STARTING)
      setErrorMessage('')

      try {
        const caps = await engine.open({ facingMode, deviceId })
        await engine.refreshLenses()

        const openedDeviceId = engine.deviceId || deviceId || selectionRef.current.deviceId
        selectionRef.current = {
          ...selectionRef.current,
          facing: facingMode,
          deviceId: openedDeviceId
        }

        setCapabilities(caps)
        // Chips follow the camera we asked for. The track's facingMode is not
        // reliable on iOS and was hiding the rear lens row after a shot.
        setFacing(facingMode)
        setActiveDeviceId(openedDeviceId)
        setChips(engine.chipsFor(facingMode))
        setCanFlip(engine.canFlip)

        const savedZoom = selectionRef.current.zoom
        let nextZoom = caps.zoom ? caps.zoom.current || caps.zoom.min : 1
        if (savedZoom != null) {
          if (caps.zoom) {
            nextZoom = Math.min(caps.zoom.max, Math.max(caps.zoom.min, savedZoom))
            await engine.setZoom(nextZoom)
          } else {
            nextZoom = Math.min(DIGITAL_ZOOM_MAX, Math.max(1, savedZoom))
          }
        }
        setZoom(nextZoom)

        if (selectionRef.current.torch && caps.torch) {
          const applied = await engine.setTorch(true)
          setTorchOn(!!applied)
        } else {
          setTorchOn(false)
        }

        attachStream(engine.stream)
        shutterPressedRef.current = false
        shutterReadyAtRef.current = Date.now() + SHUTTER_GUARD_MS
        setPhase(PHASE_LIVE)
      } catch (error) {
        const message =
          error instanceof CameraError ? error.message : error?.message || cameraErrorMessage('unknown')
        setErrorMessage(message)
        setPhase(PHASE_ERROR)
      }
    },
    [attachStream]
  )

  useEffect(() => {
    if (!CameraEngine.isSupported()) {
      setErrorMessage(cameraErrorMessage(CAMERA_ERROR_UNSUPPORTED))
      setPhase(PHASE_ERROR)
      return undefined
    }

    const engine = new CameraEngine({ audio: enabledModes.includes('video') || isVideo })
    engineRef.current = engine
    openCamera({
      facingMode: selectionRef.current.facing,
      deviceId: selectionRef.current.deviceId
    })

    return () => {
      if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
      recorderRef.current?.cancel()
      recorderRef.current = null
      if (countdownRef.current) window.clearTimeout(countdownRef.current)
      engine.close()
      engineRef.current = null
    }
    // Deliberately mount-only: reopening on prop churn would re-prompt for permission.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => releaseCapturedUrl, [releaseCapturedUrl])

  useEffect(() => {
    if (phase !== PHASE_RECORDING) {
      setRecPulse(true)
      return undefined
    }
    const id = window.setInterval(() => setRecPulse((on) => !on), 500)
    return () => window.clearInterval(id)
  }, [phase])

  useEffect(() => {
    if (share?.status !== STAGED_UPLOADING) {
      setUploadStalled(false)
      return undefined
    }
    setUploadStalled(false)
    const id = window.setTimeout(() => setUploadStalled(true), 2500)
    return () => window.clearTimeout(id)
  }, [share?.status, share?.progress, share?.phase])

  useEffect(() => {
    if (!stayOpen || !shotHoldRef.current) return undefined
    if (share?.status === STAGED_UPLOADING) {
      sawUploadRef.current = true
      return undefined
    }
    if (!sawUploadRef.current) return undefined
    shotHoldRef.current = false
    sawUploadRef.current = false
    setHoldingShot(false)
    setFreeze(null)
    return undefined
  }, [share, stayOpen])

  const applyZoom = useCallback(
    (next) => {
      const clamped = Math.min(zoomMax, Math.max(zoomMin, next))
      setZoom(clamped)
      rememberSelection({ zoom: clamped })
      if (opticalZoom) engineRef.current?.setZoom(clamped)
    },
    [opticalZoom, rememberSelection, zoomMax, zoomMin]
  )

  const handleFlip = useCallback(() => {
    const nextFacing = facing === FRONT ? BACK : FRONT
    const engine = engineRef.current
    const lens = engine?.defaultLensFor(nextFacing)
    rememberSelection({
      facing: nextFacing,
      deviceId: lens?.deviceId || '',
      factor: lens?.factor ?? null,
      zoom: null,
      torch: false
    })
    openCamera({ facingMode: nextFacing, deviceId: lens?.deviceId || '' })
  }, [facing, openCamera, rememberSelection])

  const handleChip = useCallback(
    (chip) => {
      if (chip.deviceId === activeDeviceId) return
      rememberSelection({
        facing,
        deviceId: chip.deviceId,
        factor: chip.factor,
        zoom: null
      })
      openCamera({ facingMode: facing, deviceId: chip.deviceId })
    },
    [activeDeviceId, facing, openCamera, rememberSelection]
  )

  const handleTorch = useCallback(async () => {
    const next = !torchOn
    const applied = await engineRef.current?.setTorch(next)
    if (applied) {
      setTorchOn(next)
      rememberSelection({ torch: next })
    }
  }, [rememberSelection, torchOn])

  const handleTimer = useCallback(() => {
    setTimerSecs((current) => {
      const index = TIMER_CHOICES.indexOf(current)
      return TIMER_CHOICES[(index + 1) % TIMER_CHOICES.length]
    })
  }, [])

  const resetToLive = useCallback(() => {
    releaseCapturedUrl()
    setCaptured(null)
    if (!shotHoldRef.current) setFreeze(null)
    setShareError('')
    setShutterHint('')
    setCommitting(false)
    setElapsed(0)
    capturingRef.current = false
    shutterPressedRef.current = false
    const engine = engineRef.current
    const stream = engine?.stream
    const selection = selectionRef.current
    if (streamIsLive(stream)) {
      const currentId = engine.deviceId
      if (selection.deviceId && currentId && currentId !== selection.deviceId) {
        openCamera({ facingMode: selection.facing, deviceId: selection.deviceId })
        return
      }
      const preview = videoRef.current
      if (preview && preview.srcObject !== stream) attachStream(stream)
      if (selection.zoom != null) engine.setZoom(selection.zoom)
      if (selection.torch) engine.setTorch(true)
      setPhase(PHASE_LIVE)
      shutterReadyAtRef.current = Date.now() + 150
      return
    }
    openCamera({ facingMode: selection.facing, deviceId: selection.deviceId })
  }, [attachStream, openCamera, releaseCapturedUrl])

  useEffect(() => {
    if (!registerCommands) return undefined
    registerCommands.resetToLive = resetToLive
    return () => {
      if (registerCommands.resetToLive === resetToLive) registerCommands.resetToLive = null
    }
  }, [registerCommands, resetToLive])

  const publishCapture = useCallback(
    (media) => {
      if (stayOpen) {
        const frame = grabPreviewFrame(videoRef.current)
        releaseCapturedUrl()
        shotHoldRef.current = true
        sawUploadRef.current = share?.status === STAGED_UPLOADING
        setHoldingShot(true)
        if (frame) {
          setFreeze({ url: frame, mirror: mirrorPreview })
        } else if (media.kind === 'photo' && media.blob) {
          const url = URL.createObjectURL(media.blob)
          capturedUrlRef.current = url
          setFreeze({ url, mirror: false })
        } else {
          setFreeze(null)
        }
        setCaptured(null)
        setShutterHint('')
        setPhase(PHASE_CAPTURING)
        onCaptured?.(media)
        return
      }

      releaseCapturedUrl()
      capturedUrlRef.current = URL.createObjectURL(media.blob)
      const withUrl = { ...media, url: capturedUrlRef.current }
      setCaptured(withUrl)
      setPhase(PHASE_REVIEW)
      // Safari often cannot decode a MediaRecorder blob for review while the live
      // getUserMedia pipeline is still holding the camera — black frame + 0:00.
      // Retake re-opens the camera via openCamera when stream is gone.
      if (media.kind === 'video') {
        try {
          engineRef.current?.stopStream()
        } catch {
          // ignore
        }
      }
      onCaptured?.(withUrl)
    },
    [mirrorPreview, onCaptured, releaseCapturedUrl, share?.status, stayOpen]
  )

  const takePhoto = useCallback(async () => {
    const engine = engineRef.current
    if (!engine || capturingRef.current) return

    capturingRef.current = true
    setPhase(PHASE_CAPTURING)
    setShutterHint(labels.capturing)
    const capturedAt = Date.now()

    try {
      const blob = await engine.capturePhoto({
        videoElement: videoRef.current,
        mirror: mirrorCapture && mirrorPreview,
        digitalZoom,
        fillLight: torchOn ? 'flash' : null
      })
      publishCapture({
        kind: 'photo',
        blob,
        type: blob.type || 'image/jpeg',
        ext: '.jpg',
        capturedAt
      })
    } catch (error) {
      setErrorMessage(error?.message || 'Could not capture photo.')
      setPhase(PHASE_ERROR)
    } finally {
      capturingRef.current = false
    }
  }, [digitalZoom, labels.capturing, mirrorCapture, mirrorPreview, publishCapture, torchOn])

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }, [])

  const abandonShortHold = useCallback(() => {
    recorderRef.current?.cancel()
    recorderRef.current = null
    holdModeRef.current = false
    setElapsed(0)
    setShutterHint(labels.holdLonger)
    setPhase(PHASE_LIVE)
  }, [labels.holdLonger])

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder?.isRecording) return

    // Tap-to-stop before the floor: ignore (keep recording). Hold-release before the
    // floor: discard — otherwise a quick click/hold ships a 0s clip into review.
    if (recorder.elapsedSeconds < minSeconds) {
      if (holdModeRef.current) {
        abandonShortHold()
      }
      return
    }

    try {
      const clip = await recorder.stop()
      recorderRef.current = null
      holdModeRef.current = false
      setShutterHint('')
      publishCapture({ kind: 'video', ...clip })
    } catch (error) {
      recorderRef.current = null
      holdModeRef.current = false
      setErrorMessage(error?.message || 'Could not finish the recording.')
      setPhase(PHASE_ERROR)
    }
  }, [abandonShortHold, minSeconds, publishCapture])

  const startRecording = useCallback(() => {
    const engine = engineRef.current
    if (!engine?.stream) return
    if (recorderRef.current?.isRecording) return

    // Derived from the frame size actually negotiated, so a device that gave us 1080p
    // records at 1080p rates instead of a fixed guess.
    const plan = videoCapturePlan({
      tier: currentNetworkTier(),
      trackSettings: engine.capabilities,
      maxSeconds,
      maxBitrate
    })

    const recorder = new ClipRecorder({
      stream: engine.stream,
      videoElement: videoRef.current,
      // Bake the mirrored selfie preview (and digital zoom) into the blob so review
      // and the uploaded tribute match what the guest composed — CSS scaleX does not.
      mirror: mirrorCapture && mirrorPreview,
      digitalZoom,
      bitrate: plan.bitrate,
      onTick: (seconds) => {
        setElapsed(seconds)
        if (seconds >= maxSeconds) {
          holdModeRef.current = false
          stopRecording()
        }
      }
    })

    try {
      recorder.start()
    } catch (error) {
      holdModeRef.current = false
      setErrorMessage(error?.message || 'Recording is not supported here.')
      setPhase(PHASE_ERROR)
      return
    }

    recorderRef.current = recorder
    // Keep the preview element playing; some browsers pause it when recording starts.
    attachStream(engine.stream)
    setElapsed(0)
    setShutterHint('')
    setPhase(PHASE_RECORDING)
  }, [
    attachStream,
    digitalZoom,
    maxBitrate,
    maxSeconds,
    mirrorCapture,
    mirrorPreview,
    stopRecording
  ])

  const runCountdownThen = useCallback((action) => {
    let remaining = timerSecs
    setCountdown(remaining)
    setPhase(PHASE_COUNTDOWN)

    const tick = () => {
      remaining -= 1
      if (remaining <= 0) {
        setCountdown(0)
        action()
        return
      }
      setCountdown(remaining)
      countdownRef.current = window.setTimeout(tick, 1000)
    }
    countdownRef.current = window.setTimeout(tick, 1000)
  }, [timerSecs])

  const triggerCapture = useCallback(() => {
    const action = isVideo ? startRecording : takePhoto
    if (timerSecs > 0) {
      runCountdownThen(action)
    } else {
      action()
    }
  }, [isVideo, runCountdownThen, startRecording, takePhoto, timerSecs])

  const handleShutterDown = useCallback(
    (event) => {
      if (uploadLockRef.current) return
      if (Date.now() < shutterReadyAtRef.current) return
      if (phase !== PHASE_LIVE && phase !== PHASE_RECORDING) return

      pressStartRef.current = Date.now()
      shutterPressedRef.current = true
      setShutterHint('')
      try {
        event.currentTarget?.setPointerCapture?.(event.pointerId)
      } catch {
        // Older browsers / jsdom — capture is optional.
      }

      if (!isVideo || phase !== PHASE_LIVE) return

      // Hold-to-record starts on press (after a short threshold), not on release.
      clearHoldTimer()
      holdTimerRef.current = window.setTimeout(() => {
        holdTimerRef.current = null
        holdModeRef.current = true
        // Skip self-timer for a press-and-hold gesture.
        startRecording()
      }, HOLD_TO_RECORD_MS)
    },
    [clearHoldTimer, isVideo, phase, startRecording]
  )

  const handleShutterUp = useCallback(() => {
    // Require a press that began on this shutter. A ghost pointerup from the
    // Open Camera click (retargeted onto the newly mounted shutter) must not fire.
    if (uploadLockRef.current) return
    if (!shutterPressedRef.current) return
    shutterPressedRef.current = false

    const holdWasPending = !!holdTimerRef.current
    clearHoldTimer()
    pressStartRef.current = 0

    // Prefer the recorder ref so a hold that just started is not missed while React
    // phase state is still catching up.
    if (recorderRef.current?.isRecording || phase === PHASE_RECORDING) {
      stopRecording()
      return
    }

    if (phase !== PHASE_LIVE) return

    if (isVideo) {
      // Released before the hold threshold → tap-to-start (toggle until next tap).
      if (holdWasPending) {
        holdModeRef.current = false
        triggerCapture()
      }
      return
    }

    triggerCapture()
  }, [clearHoldTimer, isVideo, phase, stopRecording, triggerCapture])

  const handleShutterCancel = useCallback(() => {
    const holdWasPending = !!holdTimerRef.current
    clearHoldTimer()
    shutterPressedRef.current = false
    if (holdModeRef.current && recorderRef.current?.isRecording) {
      stopRecording()
      return
    }
    // Lost the press before hold started — do not treat as a tap.
    if (holdWasPending) pressStartRef.current = 0
  }, [clearHoldTimer, stopRecording])

  /**
   * Share joins whatever the background transfer has already managed, so on the happy path
   * this returns almost immediately. Only from here on does the guest see progress.
   */
  const handleShare = useCallback(async () => {
    setShareError('')
    setCommitting(true)
    try {
      await onShare?.()
    } catch (error) {
      setShareError(error?.message || 'Upload failed. Try again.')
    } finally {
      setCommitting(false)
    }
  }, [onShare])

  const handleCancelUpload = useCallback(() => {
    onCancelUpload?.()
    setShareError('')
    setCommitting(false)
  }, [onCancelUpload])

  const handleRetake = useCallback(() => {
    if (committing) return
    releaseCapturedUrl()
    setCaptured(null)
    setElapsed(0)
    setShareError('')
    setShutterHint('')
    capturingRef.current = false
    shutterPressedRef.current = false
    onRetake?.()

    // Video review stops the live stream so Safari can decode the take; always reopen.
    const selection = selectionRef.current
    openCamera({ facingMode: selection.facing, deviceId: selection.deviceId })
  }, [committing, onRetake, openCamera, releaseCapturedUrl])

  const handleStageTouchStart = useCallback(
    (event) => {
      if (uploadLockRef.current) return
      if (event.touches?.length === 2) {
        pinchRef.current = { startDistance: distanceBetween(event.touches), startZoom: zoom }
      }
    },
    [zoom]
  )

  const handleStageTouchMove = useCallback(
    (event) => {
      if (uploadLockRef.current) return
      if (event.touches?.length !== 2) return
      const { startDistance, startZoom } = pinchRef.current
      if (!startDistance) return
      const ratio = distanceBetween(event.touches) / startDistance
      applyZoom(startZoom * ratio)
    },
    [applyZoom]
  )

  const handleStageClick = useCallback(
    (event) => {
      if (uploadLockRef.current) return
      if (phase !== PHASE_LIVE && phase !== PHASE_RECORDING) return

      const now = Date.now()
      if (now - lastTapRef.current < 300) {
        lastTapRef.current = 0
        applyZoom(zoom >= 2 ? zoomMin : 2)
        return
      }
      lastTapRef.current = now

      if (!capabilities?.tapToFocus) return
      const rect = stageRef.current?.getBoundingClientRect()
      if (!rect?.width || !rect?.height) return

      const x = (event.clientX - rect.left) / rect.width
      const y = (event.clientY - rect.top) / rect.height
      setFocusPoint({ left: event.clientX - rect.left, top: event.clientY - rect.top })
      window.setTimeout(() => setFocusPoint(null), 900)
      engineRef.current?.focusAt(x, y)
    },
    [applyZoom, capabilities, phase, zoom, zoomMin]
  )

  const previewTransform = `${mirrorPreview ? 'scaleX(-1)' : ''} scale(${digitalZoom})`.trim()
  const remaining = Math.max(0, Math.ceil(maxSeconds - elapsed))
  // Keep the shutter enabled while recording so hold-release / tap-to-stop still receive
  // pointerup. Early stops are ignored (tap) or discarded (hold) inside stopRecording.
  const canStop = !isVideo || elapsed >= minSeconds
  const hostUploading = share?.status === STAGED_UPLOADING
  const captureLocked = stayOpen && (holdingShot || hostUploading)
  const sharing = committing && hostUploading
  const closeLocked = sharing || captureLocked
  const sharePercent = Math.round((share?.progress || 0) * 100)
  const uploadFinishing = hostUploading && (
    share?.phase === UPLOAD_PHASE_FINALIZING || sharePercent >= 95
  )
  const uploadQuiet = hostUploading && uploadStalled && !uploadFinishing
  const uploadLabel = shareError
    || (uploadFinishing ? labels.finishing : uploadQuiet ? labels.stillUploading : labels.sharing)
  const zoomLabel = `${Number(zoom).toFixed(zoom >= 2 ? 0 : 1)}×`
  const liveStatus =
    phase === PHASE_CAPTURING
      ? shutterHint || labels.capturing
      : shutterHint || (stayOpen && phase === PHASE_LIVE && !captureLocked ? labels.ready : '')
  const uploadStatus = shareError
    || (hostUploading
      ? (uploadFinishing || !sharePercent ? uploadLabel : `${uploadLabel} ${sharePercent}%`)
      : '')
  const showUploadChip = hostUploading || holdingShot || (stayOpen && !!shareError)
  const showUploadBanner = stayOpen && phase !== PHASE_REVIEW && showUploadChip
  const showUploadMeter = (hostUploading || holdingShot) && (stayOpen || isVideo || sharePercent > 0)
  const dockMessageText = phase === PHASE_REVIEW ? uploadStatus : liveStatus
  const recStatus = phase === PHASE_RECORDING ? (
    <div className="anear-camera-timer" role="status" aria-live="polite">
      <span
        className="anear-camera-rec-dot"
        style={{ opacity: recPulse ? 1 : 0.25 }}
        aria-hidden="true"
      />
      <span>REC</span>
      <span>{remaining}s</span>
    </div>
  ) : null
  const dockMessage = (
    <div className="anear-camera-message">
      {recStatus || (
        !showUploadBanner && showUploadChip ? (
          <UploadChip
            label={shareError || uploadLabel}
            percent={sharePercent}
            showMeter={showUploadMeter}
            error={!!shareError}
            stalled={uploadQuiet}
          />
        ) : dockMessageText ? (
          <div className="anear-camera-status" data-anear-camera-indicator="">
            {dockMessageText}
          </div>
        ) : null
      )}
    </div>
  )

  if (phase === PHASE_ERROR) {
    return (
      <div className="anear-camera" role="dialog" aria-modal="true">
        <div className="anear-camera-error">
          <p>{errorMessage}</p>
          <button type="button" className="anear-camera-btn anear-camera-btn--primary" onClick={onClose}>
            {labels.close}
          </button>
        </div>
      </div>
    )
  }

  const shellClass = [
    'anear-camera',
    `anear-camera--${look}`,
    contained ? '' : 'anear-camera--viewport'
  ].filter(Boolean).join(' ')
  const stageClass = [
    'anear-camera-stage',
    phase === PHASE_RECORDING ? 'is-recording' : '',
    phase === PHASE_RECORDING && !recPulse ? 'is-recording-soft' : ''
  ].filter(Boolean).join(' ')

  return (
    <div
      className={shellClass}
      data-anear-camera-chrome={look}
      role="dialog"
      aria-modal="true"
      aria-label={labels.shutter}
      aria-busy={captureLocked ? 'true' : undefined}
    >
      <div
        className={stageClass}
        ref={stageRef}
        onTouchStart={handleStageTouchStart}
        onTouchMove={handleStageTouchMove}
        onClick={handleStageClick}
      >
        {phase === PHASE_REVIEW && captured && !stayOpen ? (
          captured.kind === 'photo' ? (
            <img className="anear-camera-review" src={captured.url} alt="" />
          ) : (
            <ReviewClip url={captured.url} durationSecs={captured.durationSecs} />
          )
        ) : (
          <video
            className="anear-camera-preview"
            style={{ transform: previewTransform }}
            ref={videoRef}
            autoPlay
            muted
            playsInline
          />
        )}
        {stayOpen && freeze?.url ? (
          <img
            className="anear-camera-freeze"
            src={freeze.url}
            alt=""
            style={freeze.mirror ? { transform: previewTransform } : undefined}
          />
        ) : null}

        {gridOn && phase !== PHASE_REVIEW && <div className="anear-camera-guides" />}

        {focusPoint && (
          <div className="anear-camera-focus" style={{ left: focusPoint.left, top: focusPoint.top }} />
        )}

        {phase === PHASE_COUNTDOWN && <div className="anear-camera-countdown">{countdown}</div>}

        {showUploadBanner ? (
          <div className="anear-camera-upload-banner">
            <UploadChip
              label={shareError || uploadLabel}
              percent={sharePercent}
              showMeter={showUploadMeter}
              error={!!shareError}
              stalled={uploadQuiet}
              prominent
            />
          </div>
        ) : null}

        {showZoomRail && phase !== PHASE_REVIEW && zoomMax > zoomMin && (
          <div className="anear-camera-zoom-rail">
            <span className="anear-camera-zoom-rail-label">{zoomLabel}</span>
            <input
              className="anear-camera-zoom"
              type="range"
              min={zoomMin}
              max={zoomMax}
              step={opticalZoom?.step || 0.1}
              value={zoom}
              aria-label={labels.zoom}
              orient="vertical"
              disabled={captureLocked}
              onChange={(event) => applyZoom(parseFloat(event.target.value))}
            />
          </div>
        )}

        {phase !== PHASE_REVIEW && (
          <div className="anear-camera-top">
            <button
              type="button"
              className="anear-camera-close anear-camera-tool"
              data-anear-camera-close=""
              onClick={onClose}
              disabled={closeLocked}
              aria-label={labels.close}
            >
              <CloseIcon />
            </button>
            {showAccessoryTools ? (
              <button
                type="button"
                className={`anear-camera-grid anear-camera-tool${gridOn ? ' is-on' : ''}`}
                onClick={() => setGridOn((on) => !on)}
                aria-pressed={gridOn}
                aria-label={labels.grid}
              >
                <GridIcon />
              </button>
            ) : (
              <span className="anear-camera-top-spacer" aria-hidden="true" />
            )}
            <div className="anear-camera-top-end">
              {showAccessoryTools && (
                <button
                  type="button"
                  className={`anear-camera-timer-toggle anear-camera-tool${timerSecs > 0 ? ' is-on' : ''}`}
                  onClick={handleTimer}
                  aria-label={timerSecs > 0 ? `${timerSecs}s` : labels.timer}
                >
                  <TimerIcon />
                  {timerSecs > 0 ? <span className="anear-camera-timer-value">{timerSecs}s</span> : null}
                </button>
              )}
              {capabilities?.torch && (
                <button
                  type="button"
                  className={`anear-camera-torch anear-camera-tool${torchOn ? ' is-on' : ''}`}
                  onClick={handleTorch}
                  aria-pressed={torchOn}
                  aria-label={labels.torch}
                >
                  <TorchIcon />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="anear-camera-controls anear-camera-dock">
          {phase === PHASE_REVIEW ? (
            <>
              {dockMessage}
              <div className="anear-camera-review-actions">
                {sharing ? (
                  <button
                    type="button"
                    className="anear-camera-cancel-upload anear-camera-btn anear-camera-btn--ghost"
                    onClick={handleCancelUpload}
                  >
                    {labels.cancelUpload}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="anear-camera-retake anear-camera-btn anear-camera-btn--ghost"
                    onClick={handleRetake}
                    disabled={committing}
                  >
                    {labels.retake}
                  </button>
                )}
                <button
                  type="button"
                  className="anear-camera-share anear-camera-btn anear-camera-btn--primary"
                  onClick={handleShare}
                  disabled={committing}
                >
                  {sharing
                    ? (uploadFinishing ? labels.finishing : `${uploadLabel} ${sharePercent}%`)
                    : labels.share}
                </button>
              </div>
            </>
          ) : (
            <>
              {chips.length > 1 && (
                <div className="anear-camera-lenses">
                  {chips.map((chip) => (
                    <button
                      key={chip.deviceId}
                      type="button"
                      className={`anear-camera-lens${chip.deviceId === activeDeviceId ? ' is-on' : ''}`}
                      onClick={() => handleChip(chip)}
                      disabled={captureLocked}
                      aria-pressed={chip.deviceId === activeDeviceId}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}

              {showModeSwitch && (
                <div className="anear-camera-modes" role="tablist" aria-label="Capture mode">
                  {['photo', 'video'].map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      role="tab"
                      className={[
                        'anear-camera-mode',
                        mode === kind ? 'is-on' : '',
                        kind === 'video' && phase === PHASE_RECORDING ? 'is-recording' : ''
                      ].filter(Boolean).join(' ')}
                      data-anear-camera-mode={kind}
                      aria-selected={mode === kind}
                      disabled={
                        phase === PHASE_RECORDING ||
                        phase === PHASE_CAPTURING ||
                        captureLocked ||
                        (!stayOpen && !!share)
                      }
                      onClick={() => onModeChange?.(kind)}
                    >
                      {kind === 'video' ? 'Video' : 'Photo'}
                    </button>
                  ))}
                </div>
              )}

              {dockMessage}

              <div className="anear-camera-shutter-row">
                <div className="anear-camera-slot">
                  {canFlip && (
                    <button
                      type="button"
                      className="anear-camera-flip anear-camera-tool"
                      onClick={handleFlip}
                      disabled={phase === PHASE_RECORDING || phase === PHASE_CAPTURING || captureLocked}
                      aria-label={labels.flip}
                    >
                      <FlipIcon />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className={[
                    'anear-camera-shutter',
                    isVideo ? 'is-video' : '',
                    phase === PHASE_RECORDING ? 'is-recording' : '',
                    phase === PHASE_RECORDING && !canStop ? 'is-armed' : ''
                  ].filter(Boolean).join(' ')}
                  onPointerDown={handleShutterDown}
                  onPointerUp={handleShutterUp}
                  onPointerCancel={handleShutterCancel}
                  disabled={
                    phase === PHASE_STARTING ||
                    phase === PHASE_COUNTDOWN ||
                    phase === PHASE_CAPTURING ||
                    captureLocked ||
                    (!stayOpen && hostUploading)
                  }
                  aria-label={
                    phase === PHASE_RECORDING ? labels.stop : isVideo ? labels.record : labels.shutter
                  }
                >
                  {phase === PHASE_RECORDING ? (
                    <span className="anear-camera-shutter-stop" aria-hidden="true" />
                  ) : isVideo ? (
                    <VideoIcon />
                  ) : null}
                </button>

                <div className="anear-camera-slot" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
